import { useEffect, useRef, useCallback } from 'react';

interface GoogleSignInButtonProps {
  onSuccess: (credential: string) => void;
  disabled?: boolean;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export const GoogleSignInButton = ({ onSuccess, disabled }: GoogleSignInButtonProps) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  const initializeGoogleSignIn = useCallback(() => {
    if (window.google && buttonRef.current) {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      
      if (!clientId) {
        console.error('Google Client ID not configured');
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: any) => {
          if (response.credential) {
            onSuccess(response.credential);
          }
        },
      });

      window.google.accounts.id.renderButton(
        buttonRef.current,
        {
          theme: 'outline',
          size: 'large',
          width: buttonRef.current.offsetWidth,
          text: 'signup_with',
          locale: 'ja',
        }
      );
    }
  }, [onSuccess]);

  useEffect(() => {
    // Google Identity Services スクリプトが既に読み込まれているかチェック
    if (window.google) {
      initializeGoogleSignIn();
      return;
    }

    // Google Identity Services スクリプトを動的に読み込み
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      initializeGoogleSignIn();
    };

    document.head.appendChild(script);

    return () => {
      // クリーンアップ
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [initializeGoogleSignIn]);

  return (
    <div className={`w-full ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div ref={buttonRef} className="w-full flex justify-center" />
    </div>
  );
};